import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { showBadgeUnlocks } from "@/lib/badges";
import { Flame, CheckCircle } from "lucide-react";

function RatingRow({ label, value, onChange }) {
  return (
    <div>
      <p className="text-xs font-medium text-slate-600 mb-1.5">{label}</p>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={`w-9 h-9 rounded-lg text-sm font-semibold transition-colors ${value >= n ? "bg-purple-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function DailyCheckinCard({ compact = false }) {
  const [form, setForm] = useState({ mood: 0, energy: 0, sleep_quality: 0, adherence: 0, notes: "" });
  const [streak, setStreak] = useState(0);
  const [doneToday, setDoneToday] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/checkins/daily/today").catch(() => ({ data: null })),
      api.get("/checkins/daily/streak").catch(() => ({ data: { streak: 0 } })),
    ]).then(([todayRes, streakRes]) => {
      if (todayRes.data) {
        setForm({
          mood: todayRes.data.mood || 0,
          energy: todayRes.data.energy || 0,
          sleep_quality: todayRes.data.sleep_quality || 0,
          adherence: todayRes.data.adherence || 0,
          notes: todayRes.data.notes || "",
        });
        setDoneToday(true);
      }
      setStreak(streakRes.data?.streak || 0);
      setLoading(false);
    });
  }, []);

  const submit = async () => {
    if (!form.mood || !form.energy) {
      toast.error("Indiquez au moins humeur et énergie");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/checkins/daily", form);
      setDoneToday(true);
      const { data } = await api.get("/checkins/daily/streak");
      setStreak(data?.streak || 0);
      toast.success("Check-in enregistré !");
      showBadgeUnlocks(res.data?.new_badges, toast);
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message;
      toast.error(msg || "Impossible d'enregistrer le check-in");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  return (
    <Card className={`border-purple-100 ${compact ? "" : "mt-6"}`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {doneToday ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Flame className="w-5 h-5 text-orange-500" />}
            <h3 className="font-semibold text-slate-900">Check-in du jour</h3>
          </div>
          {streak > 0 && (
            <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-2.5 py-1 rounded-full">
              🔥 {streak} jour{streak > 1 ? "s" : ""}
            </span>
          )}
        </div>

        <div className="space-y-4">
          <RatingRow label="Humeur" value={form.mood} onChange={(v) => setForm({ ...form, mood: v })} />
          <RatingRow label="Énergie" value={form.energy} onChange={(v) => setForm({ ...form, energy: v })} />
          {!compact && (
            <>
              <RatingRow label="Sommeil" value={form.sleep_quality} onChange={(v) => setForm({ ...form, sleep_quality: v })} />
              <RatingRow label="Adhérence au plan" value={form.adherence} onChange={(v) => setForm({ ...form, adherence: v })} />
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Notes (optionnel)"
                rows={2}
                className="resize-none text-sm"
              />
            </>
          )}
          <Button onClick={submit} disabled={submitting} className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full">
            {submitting ? "..." : doneToday ? "Mettre à jour" : "Valider le check-in"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}